import {Component, Inject, LOCALE_ID, OnDestroy, OnInit} from '@angular/core';
import gql from 'graphql-tag';
import {
  GetReportsTemplates,
  GetReportsTemplates_templates_edges,
} from './__generated__/GetReportsTemplates';
import {Apollo} from 'apollo-angular';
import {ErrorService} from '../../shared/services/error.service';
import {catchError, map, switchMap, take, withLatestFrom} from 'rxjs/operators';
import {BehaviorSubject, EMPTY, merge, of, Subject, Subscription} from 'rxjs';
import {UntypedFormControl} from '@angular/forms';
import {ActivatedRoute, Router} from '@angular/router';
import {
  getSpecificTimeSelection,
  mapMonthSelection,
  SpecificTimeSelection,
} from '../../shared/utils/date-sort.utils';
import {GetStatistics, GetStatisticsVariables} from './__generated__/GetStatistics';
import {ImageTypeEnum, ObjectTypeEnum} from '../../shared/graphql/globalTypes';

import * as Highcharts from 'highcharts/highcharts';
import addHighchartsMore from 'highcharts/highcharts-more';
import {GraphGetData, GraphGetDataVariables} from './__generated__/GraphGetData';
import {
  GetReportContentItem,
  GetReportContentItem_contentItem,
  GetReportContentItemVariables,
} from './__generated__/GetReportContentItem';
import {notNull} from '../../shared/utils/utils';
import {
  GetStatisticsOfAggregates,
  GetStatisticsOfAggregatesVariables,
} from './__generated__/GetStatisticsOfAggregates';
import {
  ReportsComponent_getTopTen,
  ReportsComponent_getTopTen_topTenContentItems,
  ReportsComponent_getTopTenVariables,
} from './__generated__/ReportsComponent_getTopTen';
import {GetAllowedUsage} from '../dashboard/__generated__/GetAllowedUsage';
import {
  allowedUsageQuery,
  TimeSelectionOptionWithAllowedUsage,
} from '../dashboard/dashboard.component';
import {DatePipe} from '@angular/common';
import moment from 'moment/moment';

addHighchartsMore(Highcharts);

interface TemplateQueryItems {
  id: string;
  title: string;
  type: string;
}

const reportContentItemGetQuery = gql`
  query GetReportContentItem($id: ID!) {
    contentItem(id: $id ) {
      id
      templateItem {
        id
        title
        template {
          id
          title
          items {
            id
          }
        }
      }
      title
      type
      views {
        totalCount
      }
    }
  }
`;

interface Statistics {
  completedPlays: number;
  createdContentCount: number;
  gifTotalCount: number;
  imageTotalCount: number;
  videoTotalCount: number;
  totalPlayAverageTime: number;
  totalPlayTotalCount: number;
  viewCount: number;
  createdChartValue: number;
}

interface FilterSelection {
  templateId: string | null;
  templateItemId: string | null;
  contentId: string | null;
  createdGte: string | null;
  createdLt: string | null;
  archived?: boolean;
}

interface TopTenFilterSelection {
  month: string | null;
  templateId: string | null;
  templateItemId: string | null;
}

const reportGetQuery = gql`
  query GetStatistics($contentId: String, $templateId: String, $templateItemId: String, $createdGte: DateTime, $createdLt: DateTime) {
    createdContentCount: content(id: $contentId, templateId: $templateId, templateItemId: $templateItemId, created_Gte: $createdGte, created_Lt: $createdLt) {
      totalCount
    }
    viewCount: views(contentId: $contentId, templateId: $templateId, templateItemId: $templateItemId, created_Gte: $createdGte, created_Lt: $createdLt) {
      totalCount
    }
    completedPlays: plays(contentId: $contentId, templateId: $templateId, templateItemId: $templateItemId, created_Gte: $createdGte, created_Lt: $createdLt, completed: true) {
      totalCount
    }
    totalPlayInfo: plays(contentId: $contentId, templateId: $templateId, templateItemId: $templateItemId, created_Gte: $createdGte, created_Lt: $createdLt) {
      totalCount
      averagePlayingTime
    }
    #    # Use this to calculate the percentage of content type. (imageTotalCount/createdContentCount) * 100 == % images etc
    imageTotalCount: content(filterType: Image, id: $contentId, templateId: $templateId, templateItemId: $templateItemId, created_Gte: $createdGte, created_Lt: $createdLt) {
      totalCount
    },
    gifTotalCount: content(filterType: GIF, id: $contentId, templateId: $templateId, templateItemId: $templateItemId, created_Gte: $createdGte, created_Lt: $createdLt) {
      totalCount
    }
    videoTotalCount: content(filterType: Video, id: $contentId, templateId: $templateId, templateItemId: $templateItemId, created_Gte: $createdGte, created_Lt: $createdLt) {
      totalCount
    }
  }
`;

const reportGetQueryOfAggegates = gql`
  query GetStatisticsOfAggregates($templateId: String, $templateItemId: String, $createdGte: DateTime, $archived: Boolean, $createdLt: DateTime) {
    me {
      id
      tenant {
        id
        statistics(templateId: $templateId, templateItemId: $templateItemId, periodGte: $createdGte, periodLt: $createdLt, archived: $archived) {
          newContentCount
          newViewsCount
        }
        imageStats: statistics(templateId: $templateId, templateItemId: $templateItemId, periodGte: $createdGte, periodLt: $createdLt, archived: $archived, contentType: Image) {
          newContentCount
          newViewsCount
        }
        gifStats: statistics(templateId: $templateId, templateItemId: $templateItemId, periodGte: $createdGte, periodLt: $createdLt, archived: $archived, contentType: GIF) {
          newContentCount
          newViewsCount
        }
        videoStats: statistics(templateId: $templateId, templateItemId: $templateItemId, periodGte: $createdGte, periodLt: $createdLt, archived: $archived, contentType: Video) {
          newContentCount
          newViewsCount
          newPlaysCount
          newPlayDuration
          newCompletedPlaysCount
        }
      }
    }
  }
`;

const getTopTen = gql`
  query ReportsComponent_getTopTen($month: String, $templateItemId: String, $templateId: String, $archived: Boolean) {
    topTenContentItems(month: $month, templateItemId: $templateItemId, templateId: $templateId, archived: $archived) {
      content {
        id
        title
        type
        templateItem {
          id
          title
          template {
            id
            title
            items {
              id
            }
          }
        }
      }
      viewCount
    }
  }
`;

const reportsTemplatesGetQuery = gql`
  query GetReportsTemplates {
    templates {
      edges {
        node {
          id
          title
          items {
            id
            title
          }
        }
      }
    }
  }
`;

const graphGetQuery = gql`
  query GraphGetData($contentId: String, $templateId: String, $templateItemId: String, $createdGte: DateTime, $createdLt: DateTime, $objectType: ObjectTypeEnum, $archived: Boolean) {
    timeserieQuery(objectType: $objectType, contentId: $contentId, templateId: $templateId, templateItemId: $templateItemId, period: {createdGte: $createdGte, createdLt: $createdLt}, archived: $archived) {
      date
      count
    }
  }
`;

@Component({
  selector: 'app-reports',
  templateUrl: './reports.component.html',
  styleUrls: ['./reports.component.scss'],
  standalone: false,
})
export class ReportsComponent implements OnInit, OnDestroy {
  createdChartValue = 0;
  deliveredChartValue = 0;
  date = new Date();
  templates: GetReportsTemplates_templates_edges[] = [];
  templateQueryItems: TemplateQueryItems[] = [];
  templateSelect = new UntypedFormControl('-1');
  specificTimeSelections: SpecificTimeSelection[] = [];
  timeSelection = new UntypedFormControl(null);
  ImageTypeEnum = ImageTypeEnum;
  ObjectTypeEnum = ObjectTypeEnum;
  initialValue = -1;

  /* eslint-disable @typescript-eslint/no-explicit-any */
  graphData: any[] = [];
  graphSelection$ = new BehaviorSubject<ObjectTypeEnum>(ObjectTypeEnum.Content);
  loading = false;
  contentId: string | null = null;
  contentItem: GetReportContentItem_contentItem | null = null;
  skipTopContent = false;
  showVideoSummary = false;

  // '-1' Only active templates. null all templates.
  templateId: string | null = '-1';
  templateItemId = '';
  hideTemplateSelect = false;
  initialRoute = true;
  private subscriptions: Subscription[] = [];

  stats: Statistics = {
    completedPlays: -1,
    createdContentCount: -1,
    gifTotalCount: -1,
    imageTotalCount: -1,
    videoTotalCount: -1,
    totalPlayAverageTime: -1,
    totalPlayTotalCount: -1,
    viewCount: -1,
    createdChartValue: -1,
  };
  filterSelection$ = new Subject<FilterSelection>();
  monthSelectionOptions: TimeSelectionOptionWithAllowedUsage[] = [];
  monthSelection = new UntypedFormControl(null);
  topTenTemplateSelect = new UntypedFormControl('-1');
  topTenMonth: string | null = moment().format('YYYY-MM');

  // '-1' Only active templates. null all templates.
  topTenTemplateId = '-1';
  topTenTemplateItemId = '';
  topTenLoading = false;
  topTenFilterSelection$ = new Subject<TopTenFilterSelection>();

  top10Content: ReportsComponent_getTopTen_topTenContentItems[] = [];

  constructor(
    private apollo: Apollo,
    private errService: ErrorService,
    private router: Router,
    @Inject(LOCALE_ID) public locale: string,
    private route: ActivatedRoute,
    private datePipe: DatePipe,
  ) {}

  ngOnInit(): void {
    if (this.locale === 'nl') {
      Highcharts.setOptions({
        lang: {
          months: [
            'Januari',
            'Februari',
            'Maart',
            'April',
            'Mei',
            'Juni',
            'Juli',
            'Augustus',
            'September',
            'October',
            'November',
            'December',
          ],
          weekdays: [
            'Zondag',
            'Maandag',
            'Dinsdag',
            'Woensdag',
            'Donderdag',
            'Vrijdag',
            'Zaterdag',
          ],
          shortMonths: [
            'Jan',
            'Feb',
            'Mrt',
            'Apr',
            'Mei',
            'Jun',
            'Jul',
            'Aug',
            'Sept',
            'Okt',
            'Nov',
            'Dec',
          ],
          loading: 'laden...',
        },
      });
    }

    this.specificTimeSelections = getSpecificTimeSelection();
    // set date selection on 'this month'
    this.timeSelection.setValue(this.specificTimeSelections[0]);

    const topTenTemplateFilter$ = merge(
      this.topTenTemplateSelect.valueChanges,
      of(this.topTenTemplateSelect.value),
    );
    const topTenMonthFilter$ = merge(
      this.monthSelection.valueChanges,
      of(this.monthSelection.value),
    );

    this.subscriptions.push(
      this.apollo
        .watchQuery<GetAllowedUsage>({
          query: allowedUsageQuery,
        })
        .valueChanges.pipe(
          catchError(error => {
            this.errService.handleDefault(error);
            return EMPTY;
          }),
        )
        .subscribe(output => {
          this.monthSelectionOptions = mapMonthSelection(output.data?.allowedUsage ?? []);
          this.monthSelection.setValue(this.monthSelectionOptions[0]);
        }),

      topTenTemplateFilter$.subscribe(output => {
        this.topTenTemplateId = output !== '-1' ? '' : '-1';
        this.topTenTemplateItemId = '';

        if (output) {
          if (output.type === 'template') {
            this.topTenTemplateId = output.id;
          }
          if (output.type === 'templateItem') {
            this.topTenTemplateItemId = output.id;
          }
        }
      }),
      topTenMonthFilter$.subscribe(output => {
        if (output) {
          this.topTenMonth = this.datePipe.transform(output.gte, 'yyyy-MM');
        }
      }),

      this.topTenFilterSelection$
        .pipe(
          switchMap(s => {
            this.topTenLoading = true;
            return this.apollo
              .watchQuery<ReportsComponent_getTopTen, ReportsComponent_getTopTenVariables>({
                query: getTopTen,
                fetchPolicy: 'network-only',
                variables: {
                  month: s.month ? s.month : null,
                  templateId: s.templateId !== '-1' ? s.templateId : undefined,
                  templateItemId: s.templateItemId,
                  archived: s.templateId === '-1' ? false : undefined,
                },
              })
              .valueChanges.pipe(
                catchError(error => {
                  this.errService.handleDefault(error);
                  return EMPTY;
                }),
              );
          }),
        )
        .subscribe(output => {
          this.top10Content = [];
          this.topTenLoading = false;
          if (output.data?.topTenContentItems) {
            this.top10Content = output.data
              .topTenContentItems as ReportsComponent_getTopTen_topTenContentItems[];
          }
        }),

      this.filterSelection$
        .pipe(
          switchMap(s => {
            this.loading = true;

            if (s.contentId) {
              return this.apollo
                .watchQuery<GetStatistics, GetStatisticsVariables>({
                  query: reportGetQuery,
                  fetchPolicy: 'network-only',
                  variables: {
                    templateId: s.templateId,
                    templateItemId: s.templateItemId,
                    contentId: s.contentId,
                    createdGte: s.createdGte ? s.createdGte : null,
                    createdLt: s.createdLt ? s.createdLt : null,
                  },
                })
                .valueChanges.pipe(
                  map(output => {
                    const data = output.data;
                    const viewCount = data?.viewCount.totalCount ?? 0;
                    const createdContentCount = data?.createdContentCount.totalCount ?? 0;
                    return {
                      completedPlays: data?.completedPlays.totalCount ?? 0,
                      createdContentCount,
                      gifTotalCount: data?.gifTotalCount.totalCount ?? 0,
                      imageTotalCount: data?.imageTotalCount.totalCount ?? 0,
                      videoTotalCount: data?.videoTotalCount.totalCount ?? 0,
                      totalPlayAverageTime: data?.totalPlayInfo.averagePlayingTime ?? 0,
                      totalPlayTotalCount: data?.totalPlayInfo.totalCount ?? 0,
                      viewCount,
                      createdChartValue: viewCount ? (createdContentCount / viewCount) * 100 : 0,
                      top10Content: [],
                    };
                  }),
                  catchError(error => {
                    this.errService.handleDefault(error);
                    this.loading = false;
                    return EMPTY;
                  }),
                );
            } else {
              return this.apollo
                .watchQuery<GetStatisticsOfAggregates, GetStatisticsOfAggregatesVariables>({
                  query: reportGetQueryOfAggegates,
                  fetchPolicy: 'network-only',
                  variables: {
                    templateId: s.templateId,
                    templateItemId: s.templateItemId,
                    createdGte: s.createdGte ? s.createdGte : null,
                    createdLt: s.createdLt ? s.createdLt : null,
                    archived: s.archived,
                  },
                })
                .valueChanges.pipe(
                  map(output => {
                    const t = output.data?.me?.tenant;
                    const viewsCount = t?.statistics.newViewsCount ?? 0;
                    const contentCount = t?.statistics.newContentCount ?? 0;

                    return {
                      completedPlays: t?.videoStats.newCompletedPlaysCount ?? 0,
                      createdContentCount: contentCount,
                      gifTotalCount: t?.gifStats.newContentCount ?? 0,
                      imageTotalCount: t?.imageStats.newContentCount ?? 0,
                      videoTotalCount: t?.videoStats.newContentCount ?? 0,
                      totalPlayAverageTime: t?.videoStats.newPlaysCount
                        ? t.videoStats.newPlayDuration / t.videoStats.newPlaysCount
                        : 0,
                      totalPlayTotalCount: t?.videoStats.newPlaysCount ?? 0,
                      viewCount: viewsCount,
                      createdChartValue: viewsCount ? (contentCount / viewsCount) * 100 : 0,
                    };
                  }),
                  catchError(error => {
                    this.errService.handleDefault(error);
                    this.loading = false;
                    return EMPTY;
                  }),
                );
            }
          }),
        )
        .subscribe(stats => {
          this.stats = stats;
          this.loading = false;
        }),
      this.route.queryParams.pipe(take(1)).subscribe(params => {
        if ('contentId' in params) {
          this.hideTemplateSelect = true;
          if (params.contentId !== '') {
            this.contentId = params.contentId;
            this.templateSelect.setValue(null);
            this.graphSelection$ = new BehaviorSubject<ObjectTypeEnum>(ObjectTypeEnum.View);
            this.skipTopContent = true;
            this.apollo
              .watchQuery<GetReportContentItem, GetReportContentItemVariables>({
                query: reportContentItemGetQuery,
                variables: {
                  id: notNull(this.contentId),
                },
              })
              .valueChanges.subscribe(output => {
                this.contentItem = output.data?.contentItem ?? null;
                this.showVideoSummary = this.contentItem?.type === this.ImageTypeEnum.Video;
              });
          }
        }
      }),
      this.apollo
        .query<GetReportsTemplates>({
          query: reportsTemplatesGetQuery,
        })
        .pipe(
          catchError(error => {
            this.errService.handleDefault(error);
            return EMPTY;
          }),
          withLatestFrom(this.route.queryParams),
        )
        .subscribe(([output, params]) => {
          this.templates = output.data?.templates.edges ?? [];
          this.templateQueryItems = [];
          this.templates.forEach(template => {
            this.templateQueryItems.push({
              id: template.node.id,
              title: template.node.title,
              type: 'template',
            });
            if (template.node.items.length > 1) {
              template.node.items.forEach(templateItem => {
                this.templateQueryItems.push({
                  id: templateItem.id,
                  title: template.node.title + ' / ' + templateItem.title,
                  type: 'templateItem',
                });
              });
            }
          });

          this.templateQueryItems.sort((a, b) => a.title.localeCompare(b.title));

          this.templateQueryItems
            ? this.topTenTemplateSelect.setValue(this.templateQueryItems[0])
            : this.topTenTemplateSelect.setValue(null);

          if (params.templateId) {
            this.templateId = params.templateId;
            this.templateSelect.setValue(
              this.templateQueryItems.find(
                item => item.type === 'template' && item.id === params.templateId,
              ),
            );
          } else if (params.templateItemId) {
            this.templateItemId = params.templateItemId;
            this.templateSelect.setValue(
              this.templateQueryItems.find(
                item => item.type === 'templateItem' && item.id === params.templateItemId,
              ),
            );
          }

          this.generateReport();
          this.generateTopTen();
        }),
      this.graphSelection$.subscribe(),
    );

    const templateFilter$ = merge(this.templateSelect.valueChanges, of(this.templateSelect.value));
    templateFilter$.subscribe(output => {
      this.templateId = output !== '-1' ? '' : '-1';
      this.templateItemId = '';
      // need to check if it's the initial route or not because output will always be null the first time.
      // Because templates/templateItems are not set correctly yet
      if (output === null || output === '-1') {
        if (this.initialRoute) {
          this.initialRoute = false;
          return;
        }
        this.router.navigate([]);
        return;
      }
      if (output.type === 'template') {
        this.templateId = output.id;
        this.router.navigate([], {queryParams: {templateId: output.id}});
      }
      if (output.type === 'templateItem') {
        this.templateItemId = output.id;
        this.router.navigate([], {queryParams: {templateItemId: output.id}});
      }
    });
  }

  generateReport(): void {
    this.getGraphData(this.graphSelection$.value);

    this.filterSelection$.next({
      templateId: this.templateId === '-1' ? null : this.templateId,
      templateItemId: this.templateItemId,
      createdGte: this.timeSelection.value ? this.timeSelection.value.gte : null,
      createdLt: this.timeSelection.value ? this.timeSelection.value.lt : null,
      contentId: this.contentId,
      archived: this.templateId === '-1' ? false : undefined,
    });
  }

  generateTopTen(): void {
    this.topTenFilterSelection$.next({
      month: this.topTenMonth,
      templateId: this.topTenTemplateId,
      templateItemId: this.topTenTemplateItemId,
    });
  }

  getGraphData(objectType: ObjectTypeEnum): void {
    this.graphSelection$.next(objectType);
    const chart = Highcharts.chart('graph-container', {
      chart: {
        type: 'areaspline',
      },
      plotOptions: {
        areaspline: {
          fillColor: {
            linearGradient: [0, 0, 0, 350],
            stops: [
              [0, 'rgba(130, 253, 200, 100)'],
              [1, 'rgba(250, 255, 252, 255)'],
            ],
          },
        },
      },
      title: {
        text: '',
      },
      loading: {
        hideDuration: 1000,
        showDuration: 1000,
      },
      xAxis: {
        type: 'datetime',
      },
      yAxis: {
        title: {
          text: null,
        },
        visible: false,
      },
      tooltip: {
        crosshairs: true,
        shared: true,
      },
      legend: {
        enabled: false,
      },
      series: [
        {
          marker: {
            enabled: false,
          },
          name: '',
          data: this.graphData,
          linecap: 'round',
          lineWidth: 4,
          zIndex: 1,
          color: '#82FDC8',
        },
      ],
      /* eslint-disable @typescript-eslint/no-explicit-any */
    } as any);

    chart.showLoading();
    this.apollo
      .watchQuery<GraphGetData, GraphGetDataVariables>({
        query: graphGetQuery,
        fetchPolicy: 'network-only',
        variables: {
          templateId: this.templateId === '-1' ? null : this.templateId,
          templateItemId: this.templateItemId,
          createdGte: this.timeSelection.value ? this.timeSelection.value.gte : null,
          createdLt: this.timeSelection.value ? this.timeSelection.value.lt : null,
          contentId: this.contentId,
          objectType,
          archived: this.templateId === '-1' ? false : undefined,
        },
      })
      .valueChanges.pipe(
        take(1),
        catchError(error => {
          this.errService.handleDefault(error);
          chart.hideLoading();
          return EMPTY;
        }),
      )
      .subscribe(output => {
        this.graphData = [];
        (output.data?.timeserieQuery ?? []).forEach(serie => {
          if (serie?.date !== undefined) {
            const newDate = new Date(serie.date);
            const milliseconds = newDate.getTime();
            const graphData = [milliseconds, serie.count];
            this.graphData.push(graphData);
          }
        });
        chart.update({
          series: [
            {
              data: this.graphData,
            },
          ],
          /* eslint-disable @typescript-eslint/no-explicit-any */
        } as any);
        chart.hideLoading();
      });
  }

  toContentView(id: string): void {
    this.router.navigate(['content', id]);
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(subscription => {
      subscription.unsubscribe();
    });
  }
}
